import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Funnel,
  FunnelChart,
  LabelList
} from "recharts";
import { 
  Activity, 
  Loader2,
  Users,
  MousePointer,
  CalendarCheck,
  TrendingUp,
  Clock,
  Eye,
  Phone,
  CheckCircle,
  XCircle,
  RefreshCw,
  Filter,
  ChevronDown,
  User,
  Calendar,
  FileText,
  Lock,
  MessageCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#B08968', '#C9A987', '#9A7558', '#D4B896', '#7A5D3E', '#E8D5C4'];

const AdminAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState("7d");
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  
  // Enhanced Analytics V2 states
  const [enhancedOverview, setEnhancedOverview] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  
  // Live Tracking states
  const [liveTracking, setLiveTracking] = useState(null);
  const [activeChats, setActiveChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [agentMessage, setAgentMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, [period]);

  // Auto-refresh live data every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchLiveData();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchLiveData = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = { Authorization: `Bearer ${token}` };

      const [liveRes, chatsRes] = await Promise.all([
        axios.get(`${API}/admin/live-tracking`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/chats/active`, { headers }).catch(() => ({ data: { chats: [] } }))
      ]);

      if (liveRes.data) setLiveTracking(liveRes.data);
      if (chatsRes.data) setActiveChats(chatsRes.data.chats || []);
    } catch (error) {
      console.error("Failed to fetch live data:", error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const headers = { Authorization: `Bearer ${token}` };

      const [overviewRes, timelineRes, funnelRes, eventsRes, sessionsRes, enhancedRes, realtimeRes, liveRes, chatsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview?period=${period}`, { headers }),
        axios.get(`${API}/admin/analytics/timeline?period=${period}`, { headers }),
        axios.get(`${API}/admin/analytics/funnel?period=${period}`, { headers }),
        axios.get(`${API}/admin/analytics/events?limit=30`, { headers }),
        axios.get(`${API}/admin/analytics/sessions?limit=15`, { headers }),
        // Enhanced Analytics V2
        axios.get(`${API}/admin/analytics-v2/overview?period=${period}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/analytics-v2/realtime?minutes=5`, { headers }).catch(() => ({ data: null })),
        // Live Tracking
        axios.get(`${API}/admin/live-tracking`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/chats/active`, { headers }).catch(() => ({ data: { chats: [] } }))
      ]);

      setOverview(overviewRes.data);
      setTimeline(timelineRes.data.timeline);
      setFunnel(funnelRes.data.funnel);
      setRecentEvents(eventsRes.data.events);
      setEventTypes(eventsRes.data.event_types);
      setSessions(sessionsRes.data.sessions);
      
      // Enhanced V2 data
      setEnhancedOverview(enhancedRes.data);
      setRealtimeData(realtimeRes.data);
      
      // Live Tracking data
      if (liveRes.data) setLiveTracking(liveRes.data);
      if (chatsRes.data) setActiveChats(chatsRes.data.chats || []);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  // Chat management functions
  const handleViewChat = async (chat) => {
    setSelectedChat(chat);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/chat/${chat.id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatMessages(response.data.messages || []);
    } catch (error) {
      toast.error("Failed to load chat messages");
    }
  };

  const handleTakeoverChat = async (chatId) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/chats/${chatId}/takeover`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Chat taken over successfully!");
      fetchLiveData();
      if (selectedChat?.id === chatId) {
        handleViewChat({ ...selectedChat, status: "human" });
      }
    } catch (error) {
      toast.error("Failed to takeover chat");
    }
  };

  const handleSendAgentMessage = async () => {
    if (!agentMessage.trim() || !selectedChat) return;
    
    setSendingMessage(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(
        `${API}/admin/chats/${selectedChat.id}/send?message=${encodeURIComponent(agentMessage)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgentMessage("");
      handleViewChat(selectedChat);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCloseChat = async (chatId) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/chats/${chatId}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Chat closed");
      setSelectedChat(null);
      fetchLiveData();
    } catch (error) {
      toast.error("Failed to close chat");
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success("Data refreshed!");
  };

  const formatDateTime = (isoStr) => {
    const date = new Date(isoStr);
    return date.toLocaleString("en-IN", { 
      day: "numeric", 
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getEventIcon = (eventName) => {
    const icons = {
      "availability_widget_viewed": <Eye className="w-3.5 h-3.5" />,
      "name_entered": <User className="w-3.5 h-3.5" />,
      "phone_entered": <Phone className="w-3.5 h-3.5" />,
      "check_in_selected": <Calendar className="w-3.5 h-3.5" />,
      "check_out_selected": <Calendar className="w-3.5 h-3.5" />,
      "check_availability_clicked": <MousePointer className="w-3.5 h-3.5" />,
      "availability_result_shown": <CheckCircle className="w-3.5 h-3.5" />,
      "page_view": <Eye className="w-3.5 h-3.5" />,
      "budget_calculated": <FileText className="w-3.5 h-3.5" />,
      "pdf_downloaded": <FileText className="w-3.5 h-3.5" />,
      "deal_locked": <Lock className="w-3.5 h-3.5" />
    };
    return icons[eventName] || <Activity className="w-3.5 h-3.5" />;
  };

  const getEventColor = (eventName) => {
    if (eventName.includes("available") || eventName.includes("success")) return "text-green-600 bg-green-50";
    if (eventName.includes("error") || eventName.includes("blocked")) return "text-red-600 bg-red-50";
    if (eventName.includes("click") || eventName.includes("submit")) return "text-blue-600 bg-blue-50";
    return "text-[#B08968] bg-[#B08968]/10";
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#B08968]" />
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Complete metrics and user activity tracking
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Period Filter */}
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="all">All Time</option>
            </select>

            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Metrics Cards */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              icon={<Users className="w-5 h-5" />}
              label="Total Visitors"
              value={overview.metrics.total_visitors}
              color="blue"
            />
            <MetricCard
              icon={<MousePointer className="w-5 h-5" />}
              label="Total Events"
              value={overview.metrics.total_events}
              color="purple"
            />
            <MetricCard
              icon={<CalendarCheck className="w-5 h-5" />}
              label="Availability Checks"
              value={overview.metrics.availability_checks}
              color="amber"
            />
            <MetricCard
              icon={<CheckCircle className="w-5 h-5" />}
              label="Available Results"
              value={overview.metrics.available_results}
              color="green"
            />
            <MetricCard
              icon={<XCircle className="w-5 h-5" />}
              label="Blocked Results"
              value={overview.metrics.blocked_results}
              color="red"
            />
            <MetricCard
              icon={<User className="w-5 h-5" />}
              label="Leads Captured"
              value={overview.metrics.availability_leads}
              color="indigo"
            />
            <MetricCard
              icon={<FileText className="w-5 h-5" />}
              label="Budget Leads"
              value={overview.metrics.budget_leads}
              color="teal"
            />
            <MetricCard
              icon={<Lock className="w-5 h-5" />}
              label="Locked Deals"
              value={overview.metrics.locked_deals}
              color="orange"
            />
          </div>
        )}

        {/* Enhanced Analytics V2 Section */}
        {enhancedOverview && (
          <div className="bg-gradient-to-r from-[#B08968]/5 to-[#C9A987]/5 rounded-xl border border-[#B08968]/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#B08968]" />
                Enhanced Behavior Analytics
              </h2>
              {realtimeData && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="animate-pulse w-2 h-2 bg-green-500 rounded-full"></span>
                  <span className="text-green-600 font-medium">
                    {realtimeData.active_visitors} active now
                  </span>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-[#B08968]">{enhancedOverview.metrics.page_views}</div>
                <div className="text-xs text-gray-600">Page Views</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-blue-600">{enhancedOverview.metrics.cta_clicks}</div>
                <div className="text-xs text-gray-600">CTA Clicks</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-purple-600">{enhancedOverview.metrics.form_starts}</div>
                <div className="text-xs text-gray-600">Form Starts</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-green-600">{enhancedOverview.metrics.form_submits}</div>
                <div className="text-xs text-gray-600">Form Submits</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-amber-600">{enhancedOverview.metrics.exit_intents}</div>
                <div className="text-xs text-gray-600">Exit Intents</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-red-600">{enhancedOverview.metrics.rage_clicks}</div>
                <div className="text-xs text-gray-600">Rage Clicks</div>
              </div>
            </div>

            {/* Device & Scroll Depth Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {/* Device Breakdown */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Device Breakdown</h4>
                <div className="space-y-2">
                  {Object.entries(enhancedOverview.device_breakdown || {}).map(([device, count]) => (
                    <div key={device} className="flex items-center justify-between">
                      <span className="text-sm capitalize text-gray-600">{device}</span>
                      <span className="text-sm font-medium text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Scroll Depth */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Scroll Depth</h4>
                <div className="space-y-2">
                  {Object.entries(enhancedOverview.scroll_depth_breakdown || {}).map(([depth, count]) => (
                    <div key={depth} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{depth}%</span>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#B08968] rounded-full"
                            style={{ width: `${parseInt(depth)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-8">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* UTM Sources */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Traffic Sources</h4>
                <div className="space-y-2">
                  {Object.entries(enhancedOverview.utm_sources || {}).slice(0, 5).map(([source, count]) => (
                    <div key={source} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 truncate max-w-[120px]">{source}</span>
                      <span className="text-sm font-medium text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Live Tracking & Chat Management Section */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Live Tracking & Chat Management
              </h2>
              <div className="flex items-center gap-4 text-sm">
                {liveTracking && (
                  <>
                    <span className="text-gray-600">
                      <span className="font-semibold text-green-600">{liveTracking.stats.active_visitors}</span> Active
                    </span>
                    <span className="text-gray-600">
                      <span className="font-semibold text-blue-600">{liveTracking.stats.currently_chatting}</span> Chatting
                    </span>
                    <span className="text-gray-600">
                      <span className="font-semibold text-amber-600">{liveTracking.stats.high_intent_visitors}</span> High Intent
                    </span>
                    {liveTracking.stats.waiting_for_handover > 0 && (
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium animate-pulse">
                        {liveTracking.stats.waiting_for_handover} Waiting for Agent!
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 divide-x">
            {/* Active Sessions Panel */}
            <div className="p-4">
              <h3 className="font-medium text-gray-700 mb-3 text-sm">Live Visitors</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {liveTracking?.active_sessions?.length > 0 ? (
                  liveTracking.active_sessions.map((session, idx) => (
                    <div 
                      key={session.session_id || idx}
                      className={`p-3 rounded-lg border transition-all ${
                        session.chat_status === 'waiting_handover' 
                          ? 'bg-red-50 border-red-200 animate-pulse' 
                          : session.intent?.high_intent_ready > 50
                          ? 'bg-amber-50 border-amber-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            session.chat_status ? 'bg-blue-500' : 'bg-green-500'
                          }`} />
                          <span className="text-sm font-medium text-gray-900">
                            {session.device_info?.device_type || 'Unknown'} visitor
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {session.current_page || '/weddings'}
                        </span>
                      </div>
                      
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {session.intent && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            session.intent.primary_intent === 'ready_to_book' 
                              ? 'bg-green-100 text-green-700'
                              : session.intent.primary_intent === 'comparing'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {session.intent.primary_intent}
                          </span>
                        )}
                        {session.chat_status && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            session.chat_status === 'waiting_handover'
                              ? 'bg-red-100 text-red-700'
                              : session.chat_status === 'human'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            Chat: {session.chat_status}
                          </span>
                        )}
                        {session.chat_id && session.chat_status === 'waiting_handover' && (
                          <Button
                            size="sm"
                            className="h-6 text-xs bg-[#B08968] hover:bg-[#9A7858]"
                            onClick={() => handleTakeoverChat(session.chat_id)}
                          >
                            Take Over
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No active visitors right now</p>
                  </div>
                )}
              </div>
            </div>

            {/* Active Chats Panel */}
            <div className="p-4">
              <h3 className="font-medium text-gray-700 mb-3 text-sm">Active Chats</h3>
              
              {selectedChat ? (
                <div className="space-y-3">
                  {/* Chat Header */}
                  <div className="flex items-center justify-between p-2 bg-gray-100 rounded-lg">
                    <div>
                      <span className="font-medium text-sm">{selectedChat.user_name || 'Visitor'}</span>
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                        selectedChat.status === 'human' ? 'bg-blue-100 text-blue-700' :
                        selectedChat.status === 'waiting_handover' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {selectedChat.status}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {selectedChat.status !== 'human' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => handleTakeoverChat(selectedChat.id)}
                        >
                          Take Over
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => setSelectedChat(null)}
                      >
                        Back
                      </Button>
                    </div>
                  </div>

                  {/* Requirements Summary */}
                  {selectedChat.requirements && Object.values(selectedChat.requirements).some(v => v) && (
                    <div className="p-2 bg-amber-50 rounded-lg text-xs">
                      <div className="font-medium text-amber-800 mb-1">Captured Requirements:</div>
                      <div className="grid grid-cols-2 gap-1 text-amber-700">
                        {selectedChat.requirements.guest_count && (
                          <span>👥 {selectedChat.requirements.guest_count}</span>
                        )}
                        {selectedChat.requirements.budget_range && (
                          <span>💰 {selectedChat.requirements.budget_range}</span>
                        )}
                        {selectedChat.requirements.stay_duration && (
                          <span>🏠 {selectedChat.requirements.stay_duration}</span>
                        )}
                        {selectedChat.requirements.celebration_type && (
                          <span>🎉 {selectedChat.requirements.celebration_type}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Messages */}
                  <div className="h-[200px] overflow-y-auto space-y-2 p-2 bg-gray-50 rounded-lg">
                    {chatMessages.map((msg, idx) => (
                      <div
                        key={msg.id || idx}
                        className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                          msg.sender === 'user' ? 'bg-[#B08968] text-white' :
                          msg.sender === 'agent' ? 'bg-blue-500 text-white' :
                          'bg-white border'
                        }`}>
                          {msg.message}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Agent Input */}
                  {selectedChat.status === 'human' && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={agentMessage}
                        onChange={(e) => setAgentMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendAgentMessage()}
                        placeholder="Type your reply..."
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      />
                      <Button
                        size="sm"
                        className="bg-[#B08968] hover:bg-[#9A7858]"
                        onClick={handleSendAgentMessage}
                        disabled={sendingMessage}
                      >
                        Send
                      </Button>
                    </div>
                  )}

                  {/* Close Chat Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => handleCloseChat(selectedChat.id)}
                  >
                    Close Chat
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {activeChats.length > 0 ? (
                    activeChats.map((chat, idx) => (
                      <div
                        key={chat.id || idx}
                        className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-all ${
                          chat.status === 'waiting_handover' 
                            ? 'bg-amber-50 border-amber-300 animate-pulse'
                            : chat.status === 'human'
                            ? 'bg-blue-50 border-blue-200'
                            : 'bg-white border-gray-200'
                        }`}
                        onClick={() => handleViewChat(chat)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{chat.user_name || 'Visitor'}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            chat.status === 'waiting_handover' ? 'bg-amber-200 text-amber-800' :
                            chat.status === 'human' ? 'bg-blue-200 text-blue-800' :
                            'bg-gray-200 text-gray-600'
                          }`}>
                            {chat.status}
                          </span>
                        </div>
                        {chat.last_message && (
                          <p className="text-xs text-gray-500 mt-1 truncate">
                            {chat.last_message.message}
                          </p>
                        )}
                        {chat.intent && (
                          <div className="mt-2 flex gap-1">
                            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                              Intent: {chat.intent.primary_intent}
                            </span>
                            {chat.intent.high_intent_ready > 50 && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                High Intent
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No active chats</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity Timeline */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#B08968]" />
              Activity Timeline
            </h3>
            {timeline.length > 0 ? (
              <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <LineChart data={timeline}>
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(val) => val.slice(5)}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="visitors" 
                      stroke="#B08968" 
                      strokeWidth={2}
                      dot={{ fill: '#B08968', r: 3 }}
                      name="Visitors"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="events" 
                      stroke="#9A7558" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: '#9A7558', r: 3 }}
                      name="Events"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-400">
                No data for this period
              </div>
            )}
          </div>

          {/* Event Breakdown Pie Chart */}
          {overview && Object.keys(overview.event_breakdown).length > 0 && (
            <div className="bg-white rounded-lg border p-6">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <MousePointer className="w-4 h-4 text-[#B08968]" />
                Event Breakdown
              </h3>
              <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={Object.entries(overview.event_breakdown).map(([name, value]) => ({
                        name: name.replace(/_/g, ' '),
                        value
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {Object.keys(overview.event_breakdown).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-2 mt-2 justify-center">
                {Object.entries(overview.event_breakdown).slice(0, 6).map(([name, value], index) => (
                  <span key={name} className="text-xs flex items-center gap-1">
                    <span 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    {name.replace(/_/g, ' ')}: {value}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Conversion Funnel */}
        {funnel.length > 0 && (
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#B08968]" />
              Conversion Funnel
            </h3>
            <div className="space-y-3">
              {funnel.map((stage, index) => (
                <div key={stage.stage} className="flex items-center gap-4">
                  <div className="w-40 text-sm text-gray-600 truncate">{stage.stage}</div>
                  <div className="flex-1">
                    <div className="h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                      <div 
                        className="h-full rounded-lg transition-all duration-500"
                        style={{ 
                          width: `${stage.percentage}%`,
                          backgroundColor: COLORS[index % COLORS.length]
                        }}
                      />
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-medium">
                        {stage.count} ({stage.percentage}%)
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom Row: Recent Events & Sessions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity Events */}
          <div className="bg-white rounded-lg border">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#B08968]" />
                Recent Activity
              </h3>
              <span className="text-xs text-gray-400">{recentEvents.length} events</span>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {recentEvents.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  No recent events
                </div>
              ) : (
                <div className="divide-y">
                  {recentEvents.map((event) => (
                    <div key={event.id} className="p-3 hover:bg-gray-50">
                      <div className="flex items-start gap-3">
                        <div className={`p-1.5 rounded ${getEventColor(event.event_name)}`}>
                          {getEventIcon(event.event_name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {event.event_name.replace(/_/g, ' ')}
                          </p>
                          {event.event_payload && Object.keys(event.event_payload).length > 0 && (
                            <p className="text-xs text-gray-500 truncate mt-0.5">
                              {JSON.stringify(event.event_payload).slice(0, 50)}...
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDateTime(event.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Sessions */}
          <div className="bg-white rounded-lg border">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#B08968]" />
                Recent Sessions
              </h3>
              <span className="text-xs text-gray-400">{sessions.length} sessions</span>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {sessions.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  No sessions yet
                </div>
              ) : (
                <div className="divide-y">
                  {sessions.map((session) => (
                    <div key={session.session_id} className="p-3 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {session.customer_name || 'Anonymous Visitor'}
                          </p>
                          {session.phone_number && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3" />
                              {session.phone_number}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {session.event_count} events • Last: {formatDateTime(session.last_seen)}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          session.lead_id ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {session.lead_id ? 'Lead' : 'Visitor'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

// Metric Card Component
const MetricCard = ({ icon, label, value, color }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
    amber: "bg-amber-50 text-amber-600",
    green: "bg-green-50 text-green-600",
    red: "bg-red-50 text-red-600",
    indigo: "bg-indigo-50 text-indigo-600",
    teal: "bg-teal-50 text-teal-600",
    orange: "bg-orange-50 text-orange-600"
  };

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsDashboard;
